import axios from 'axios';
import React, { useState } from 'react';
import { CgSpinner } from 'react-icons/cg';
import { FaCheck } from 'react-icons/fa';
import { RxCross1 } from 'react-icons/rx';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { API } from '../../utils/constants';
import { useAuth } from '../../utils/context';
import Footer from '../Footer';
import Navbar from '../Navbar';
import PackFaq from './PackFaq';

const PriceCard = ({ _id, nameOfPlan, bgColor, amount, description, benefits }) => {
  const [auth, setAuth] = useAuth();
  const navigate = useNavigate();

  // States for normal package selection
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [loading, setLoading] = useState(false);

  // States for coupon modal
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [couponSelectedOptions, setCouponSelectedOptions] = useState([{ value: 'General Test', label: 'General Test' }]);
  const [couponErrorMessage, setCouponErrorMessage] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [couponVerified, setCouponVerified] = useState(false);

  const options = [
    { value: 'General Test', label: 'General Test' },
    { value: 'Mathematics', label: 'Mathematics' },
    { value: 'Physics', label: 'Physics' },
    { value: 'Chemistry', label: 'Chemistry' },
    { value: 'Biology', label: 'Biology' },
    { value: 'Accountancy', label: 'Accountancy' },
    { value: 'Economics', label: 'Economics' },
    { value: 'Business Studies', label: 'Business Studies' },
    { value: 'History', label: 'History' },
    { value: 'Political Science', label: 'Political Science' },
    { value: 'Geography', label: 'Geography' },
    { value: 'Psychology', label: 'Psychology' },
    { value: 'Sociology', label: 'Sociology' },
    { value: 'Physical Education', label: 'Physical Education' },
  ];

  if(showCouponModal){
    options.push({ value: 'General English', label: 'General English' });
  }

  const closeModal = () => {
    setLoading(false);
    setShowOptions(false);
    setSelectedOptions([]);
  };

  const closeCouponModal = () => {
    setCouponLoading(false);
    setShowCouponModal(false);
    setCouponInput('');
    setCouponVerified(false);
    setCouponSelectedOptions([{ value: 'General Test', label: 'General Test' }]);
    setCouponErrorMessage('');
  };

  // Normal package selection change handler
  const handleSelectChange = (selected) => {
    if (nameOfPlan === 'Edutri PACK' && selected.length > 4) {
      setErrorMessage('Select only four subjects.');
    } else {
      setSelectedOptions(selected);
      setErrorMessage('');
    }
  };

  // Coupon modal selection change handler
  const handleCouponSelectChange = (selected) => {
    // Always force "General Test" to be included.
    const forced = { value: 'General Test', label: 'General Test' };
    // Filter out any instance of General Test from the new selection.
    let additional = (selected || []).filter(option => option.value !== 'General Test');
    if (additional.length > 1) {
      setCouponErrorMessage('You can only select one additional subject.');
      return;
    }
    setCouponErrorMessage('');
    setCouponSelectedOptions([forced, ...additional]);
  };

  const renderIcon = (benefit) => {
    switch (benefit) {
      case 'Both General English & General Test':
      case 'Unlimited Attempts ':
      case 'Full Access to Prep Modules':
      case 'Over 2000 questions per subject':
      case '15 Practice Tests':
      case '15 Mock Tests':
      case '15 Practice Tests for each Subject':
      case '15 Mock Tests for each Subject':
      case 'Full Access to Practice Tests':
      case 'Any three Domain Subjects':
      case 'Total five Subjects':
        return <FaCheck className='text-green-400' />;
      case '/ Any one Domain Subject':
      case '/ Any two Domain Subjects':
        return <span>&ensp;&ensp;</span>;
      default:
        return <FaCheck className='text-green-400' />;
    }
  };

  const initPayment = async () => {
    if (nameOfPlan === 'Edutri PACK' && selectedOptions.length < 3) {
      toast.error('Select atleast 4 options.');
      return;
    }
    setLoading(true);
    try {
      const keyRes = await axios.get(`${API}/payment/getKey`);
      const key = keyRes.data; // assuming the key is returned in data
      const response = await axios.post(`${API}/payment/initiate`, {
        packageId: _id,
        userId: auth.user?._id,
        selectedOptions: (() => {
          if (nameOfPlan === 'Edutri PACK') {
            selectedOptions.push({ value: 'General English', label: 'General English' });
          }
          return selectedOptions.map(option => option.value);
        })()
      });
      const { data } = response;
      console.log(data);

      const optionsObj = {
        key,
        amount: data.amount,
        currency: data.currency,
        name: nameOfPlan,
        description: 'Test Transaction',
        image: '',
        order_id: data.order_id,
        handler: async (response) => {
          try {
            const verifyResponse = await axios.put(`${API}/payment/verify`, {
              userId: auth.user?._id,
              email: auth.user?.email,
              name: auth?.user?.name,
              amount,
              packname: nameOfPlan,
              contact: auth?.user?.contact,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              packageId: _id,
              selectedSubjects: selectedOptions.map(option => option.value),
            });
            if (verifyResponse.data.success) {
              const userResponse = await axios.get(`${API}/users/find/${auth.user?._id}`);
              const updatedUser = userResponse.data;
              const updatedAuth = { ...auth, user: updatedUser, password: undefined };
              setAuth(updatedAuth);
              localStorage.setItem('auth', JSON.stringify(updatedAuth));
              setLoading(false);
              toast.success('Pack bought successfully');
              setTimeout(() => {
                navigate('/');
              }, 2000);
            }
          } catch (error) {
            setLoading(false);
            toast.error('Cannot process your request at the moment. Please try again later');
            console.error(error);
          }
        },
        theme: {
          color: '#3399cc'
        }
      };
      const rzp1 = new window.Razorpay(optionsObj);
      setLoading(false);
      rzp1.open();
      setSelectedOptions([]);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  // Verify coupon code input
  const verifyCoupon = () => {
    if (couponInput.trim() === 'EDUTRIFOUNDATION') {
      setCouponVerified(true);
      toast.success('Coupon code applied successfully');
    } else {
      toast.error('Invalid coupon code');
    }
  };

  // Apply coupon API call after subject selection
  const applyCoupon = async () => {
    if (!auth.user) {
      navigate('/login');
      return;
    }
    setCouponLoading(true);
    try {
      const response = await axios.post(`${API}/coupon/apply-coupon`, {
        email: auth.user?.email,
        couponCode: couponInput,
        selectedSubjects: couponSelectedOptions.map(option => option.value)
      });
      if (response.data.success) {
        toast.success(response.data.message || 'Coupon applied successfully. Subjects assigned for 7 days.');
        const userResponse = await axios.get(`${API}/users/find/${auth.user?._id}`);
        const updatedUser = userResponse.data;
        const updatedAuth = { ...auth, user: updatedUser, password: undefined };
        setAuth(updatedAuth);
        localStorage.setItem('auth', JSON.stringify(updatedAuth));
        setCouponLoading(false);
        closeCouponModal();
        setTimeout(() => {
          navigate('/');
        }, 1500);
      }
    } catch (error) {
      setCouponLoading(false);
      toast.error(error.response?.data?.message || 'Failed to apply coupon. Please try again.');
      console.error(error);
    }
  };

  return (
    <div id='select' className="price-card flex-col-reverse lg:flex-row lg:h-[550px] my-4 items-center flex">
      <div className="max-lg:hidden rounded-3xl flex flex-col justify-center px-10 hover:scale-105 shadow-2xl transition-all duration-100 p-5 relative lg:left-5 z-0 bg-white h-[450px]">
        <h4 className="font-bold text-[25px]">
          Plan Benefits:
          <hr className="my-1" />
        </h4>
        <div className="font-medium text-[13px] md:text-[18px]">
          {benefits.map((benefit, index) => (
            <p key={index} className="my-4">
              {renderIcon(benefit)}
              <span className="ml-3">{benefit}</span>
            </p>
          ))}
        </div>
      </div>

      <div className="p-4 md:max-w-[350px] max-lg:w-full relative z-10 md:max-h-[700px] max-h-[800px] bg-white transition-all duration-100 shadow-2xl my-8 rounded-3xl text-center text-black mx-3">
        <div className="p-2 py-3">
          <h2 style={{ background: bgColor }} className="text-[25px] mb-6 border-dashed border-2 border-blue-950 p-2 mx-auto text-black whitespace-nowrap rounded-3xl">
            {nameOfPlan}
          </h2>
          <hr className="my-2" />
          <div className="my-4">
            <span className="line-through text-red-500 text-xl mr-2">Rs 3999</span>
            <span className="text-green-600 text-2xl font-bold">Rs 750</span>
            <span className="text-gray-400 font-medium text-xl"> / year</span>
            <p className="ml-0 text-[12px]">*Offer price from Edutri</p>
          </div>
          <hr className="my-2" />
          <div className="md:hidden font-medium text-[13px] md:text-[18px]">
            {benefits.map((benefit, index) => (
              <p key={index} className="mt-4">
                {renderIcon(benefit)}
                <span className="ml-3">{benefit}</span>
              </p>
            ))}
          </div>
          <p className="max-md:hidden text-[15px] text-slate-600 px-1 md:h-[100px]">{description}</p>
          <div className="md:pt-8 pt-5">
            <div onClick={() => {
              if (!auth.user) {
                navigate('/login');
                return;
              }
              setShowOptions(true);
            }} className="cursor-pointer">
              <p className="w-full py-4 border transition-colors duration-100 hover:border-blue-800 border-[#23bd68] bg-[#23bd68] hover:bg-blue-600 md:mt-8 rounded-xl text-white">
                <span className="font-medium">Get Started</span>
              </p>
            </div>
            {/* Coupon Code Button */}
            <div onClick={() => {
              if (!auth.user) {
                navigate('/login');
                return;
              }
              setShowCouponModal(true);
            }} className="cursor-pointer">
              <p className="w-full py-4 border transition-colors duration-100 hover:border-blue-800 border-[#ff9800] bg-[#ff9800] hover:bg-orange-600 md:mt-4 rounded-xl text-white">
                <span className="font-medium">Apply Coupon Code</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Normal Package Modal */}
      {showOptions && (
        <div className="popup-overlay">
          <div className="rounded-3xl bg-[#ffffffee] md:w-[700px] p-4 px-6 max-md:px-2 max-md:mx-6 backdrop-blur-[100px] shadow-2xl">
            <div className="flex justify-between">
              <h2 className="font-semibold mx-2 md:text-xl whitespace-nowrap max-md:text-[17px]">
                Select Subjects for <span className="px-4 py-2 rounded-3xl max-md:px-2 max-md:text-[16px]" style={{ background: bgColor }}>{nameOfPlan}</span>
              </h2>
              <h3 className="mx-4 cursor-pointer" onClick={closeModal}><RxCross1 /></h3>
            </div>
            <hr className="my-1" />
            <div className="flex flex-col justify-center items-center">
              {(nameOfPlan === 'Edutri PACK' || nameOfPlan === 'JUMBO PACK') && (
                <p className="font-medium max-md:text-center text-[18px] text-gray-700">
                  General English is included in this pack
                </p>
              )}
              <p className="font-medium text-[18px]">
                Select any {nameOfPlan === 'Edutri PACK' && (<span>four apart from General English</span>)}
              </p>
              <div className="mt-4 w-[95%] relative">
                <Select
                  isMulti
                  options={options}
                  value={selectedOptions}
                  onChange={handleSelectChange}
                  maxMenuHeight={180}
                  minMenuHeight={10}
                />
                <div className="text-red-600 text-l mt-2">{errorMessage}</div>
              </div>
            </div>
            <div className="flex mx-4 my-3 justify-end">
              <div onClick={initPayment} className={`flex cursor-pointer mt-4 border-[2px] border-solid border-[#2fa062] bg-[#23bd68] hover:text-white px-3 py-2 font-medium rounded-md hover:border-blue-600 hover:bg-blue-600 w-[200px] mx-auto justify-center ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
                {loading && (<CgSpinner size={20} className="mt-1 animate-spin" />)}
                {!loading && <span>Confirm Selection</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Coupon Modal */}
      {showCouponModal && (
        <div className="popup-overlay">
          <div className="rounded-3xl bg-[#ffffffee] md:w-[700px] p-4 px-6 max-md:px-2 max-md:mx-6 backdrop-blur-[100px] shadow-2xl">
            <div className="flex justify-between">
              <h2 className="font-semibold mx-2 md:text-xl whitespace-nowrap max-md:text-[17px]">
                Apply Coupon Code for <span className="px-4 py-2 rounded-3xl max-md:px-2 max-md:text-[16px]" style={{ background: bgColor }}>{nameOfPlan}</span>
              </h2>
              <h3 className="mx-4 cursor-pointer" onClick={closeCouponModal}><RxCross1 /></h3>
            </div>
            <hr className="my-1" />
            <div className="flex flex-col justify-center items-center">
              {!couponVerified && (
                <>
                  <input
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value)}
                    className="border p-2 rounded-md w-[80%] mb-4"
                  />
                  <div onClick={verifyCoupon} className="cursor-pointer w-[200px] py-2 border border-blue-500 bg-blue-500 text-white rounded-md text-center">
                    Verify Coupon Code
                  </div>
                </>
              )}
              {couponVerified && (
                <>
                  <p className="font-medium text-[18px] text-gray-700 mb-4">
                    Coupon code applied successfully!
                  </p>
                  <p className="font-medium text-[18px] text-gray-700">
                    General Test is pre-selected. You can select one additional subject.
                  </p>
                  <div className="mt-4 w-[95%] relative">
                    <Select
                      isMulti
                      options={options}
                      value={couponSelectedOptions}
                      onChange={handleCouponSelectChange}
                      maxMenuHeight={180}
                      minMenuHeight={10}
                    />
                    <div className="text-red-600 text-l mt-2">{couponErrorMessage}</div>
                  </div>
                </>
              )}
            </div>
            {couponVerified && (
              <div className="flex mx-4 my-3 justify-end">
                <div onClick={applyCoupon} className={`flex cursor-pointer mt-4 border-[2px] border-solid border-[#ff9800] bg-[#ff9800] hover:text-white px-3 py-2 font-medium rounded-md hover:border-orange-600 hover:bg-orange-600 w-[200px] mx-auto justify-center ${couponLoading ? 'opacity-50 pointer-events-none' : ''}`}>
                  {couponLoading && (<CgSpinner size={20} className="mt-1 animate-spin" />)}
                  {!couponLoading && <span>Confirm Coupon</span>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PriceCardsContainer = ({ packages }) => {
  return (
    <div className="price-cards-container md:pb-6">
      {packages.map((packageItem) => (
        <PriceCard key={packageItem._id} {...packageItem} />
      ))}
    </div>
  );
};

const PriceCardPage = ({ packages }) => {
  const initialActiveTabId = packages.find((packageItem) => packageItem.nameOfPlan === 'Edutri PACK')._id;
  const [activeTab] = useState(initialActiveTabId);
  const [bgColor] = useState(packages.find(packageItem => packageItem._id === initialActiveTabId).bgColor);

  return (
    <div className="overflow-hidden max-w-full mx-auto bg-[#c4e9f0]" style={{ background: `linear-gradient(to bottom, ${bgColor}, white 30%, white)`, transition: 'background-color 0.3s ease' }}>
      <Navbar />
      <div className="flex mt-10 flex-col justify-center items-center">
        <PriceCardsContainer packages={packages.filter((packageItem) => packageItem._id === activeTab)} />
        <div style={{ background: 'linear-gradient(to bottom, #ededed, white)', transition: 'background-color 0.3s ease', width: '100%' }}>
          <PackFaq />
        </div>
        <Footer />
      </div>
      <ToastContainer />
    </div>
  );
};

const Packages = [
  {
    _id: '67d965c6b1aaa2785951d943',
    nameOfPlan: 'Edutri PACK',
    amount: 750,
    description: 'Unlock comprehensive preparation with Edutri\'s Pack! Get 75 meticulously curated mock papers spanning all domain subjects and language sections, designed to elevate your exam readiness.',
    benefits: [
      'Option A: General English and 4 Domain Subjects',
      'Option B: General English, General Test and 3 Domain Subjects',
      'Total Five Subjects',
      'Full Access to Prep Modules',
      '15 Practice Tests for each Subject',
      '15 Mock Tests for each Subject',
      'Over 2000 questions per Subject',
      'Unlimited Attempts '
    ],
    bgColor: 'rgb(217, 196, 240, 0.6)'
  }
];

export default function App() {
  return (
    <div>
      <PriceCardPage packages={Packages} />
    </div>
  );
}
